import type { ConditionRow } from './types';

/**
 * Block 12 PR-D-A Phase D4 Step 6 — defaults для раздела «Условия».
 *
 * 5 default-строк per PRD `docs/prd/v4/10-block-12-calculator.md`
 * §10.12.10:407-411 + decisions Aibek 2026-05-18:
 *   - production-time: unit='рабочих дней' (numeric input — Phase D10 Bug #14:
 *     prototype 2.12.10:208 fix к более точной формулировке «рабочих дней»,
 *     не «дни» — мебельные сроки традиционно меряются рабочими днями
 *     без учёта выходных)
 *   - advance: unit='₸' (numeric input — prototype-faithful, PRD «сумма» branch)
 *   - remainder: unit='₸', readOnly auto-computed `Math.max(0, cost − advance)`
 *     (display-only — НЕ хранится в state, derive в SectionConditions render)
 *   - warranty: unit='мес' (fixed; chip-segment {месяцев|лет} — BACKLOG B-120,
 *     priority bumped low→high после Audit Round 4)
 *   - special-conditions: unit=undefined (свободный текст)
 *
 * Stable kebab-case IDs (mirror compositionRows.ts pattern). Custom rows
 * через `genUuid()` (см. `@/lib/uuid`).
 *
 * Phase D5 Step 8 anketa-seeding не overwrite'ит conditions defaults —
 * Условия per-КП, не per-anketa.
 */
export const DEFAULT_CONDITIONS: ReadonlyArray<ConditionRow> = [
  { id: 'production-time', label: 'Срок изготовления', value: '', unit: 'рабочих дней' },
  { id: 'advance', label: 'Размер аванса', value: '', unit: '₸' },
  { id: 'remainder', label: 'Остаток', value: '', unit: '₸' },
  { id: 'warranty', label: 'Срок гарантии', value: '', unit: 'мес' },
  { id: 'special-conditions', label: 'Особые условия', value: '', unit: undefined },
];

/**
 * `remainder` уникален — value auto-computed в SectionConditions render
 * (display-only, readonly). Этот id используется UI для switch:
 *  - input.readOnly = true
 *  - value = `Math.max(0, (state.costWithMargin ?? 0) - Number(advance.value || 0))`
 *
 * NB: НЕ хранится в state.conditions[remainder].value — каждое чтение
 * рассчитывает с нуля. Защищает от рассинхронизации `advance`/`cost`/`remainder`.
 */
export const REMAINDER_ROW_ID = 'remainder';
export const ADVANCE_ROW_ID = 'advance';

/** Фронт-уровневый pure-helper для remainder calculation (используется в UI + тестах). */
export function computeRemainder(costWithMargin: number | null, advanceValueRaw: string): number {
  const cost = costWithMargin ?? 0;
  const advance = Number(advanceValueRaw);
  const advanceSafe = Number.isFinite(advance) ? advance : 0;
  return Math.max(0, cost - advanceSafe);
}

/** Поле row, поддерживающее edit (Phase D4 reducer action CONDITION_UPDATE). */
export type ConditionField = 'label' | 'value' | 'unit';
